import type {ReactElement, SyntheticEvent} from 'react'
import {cloneElement, isValidElement, useCallback, useEffect, useId, useRef} from 'react'

import styles from './SidebarTooltip.module.css'

export interface SidebarTooltipProps {
  /** Tooltip text shown when the sidebar is collapsed. */
  text: string
  /**
   * A single React element that acts as the tooltip trigger. Event handlers
   * and `aria-describedby` are cloned onto it so no wrapper element is
   * injected into the DOM — the child remains the direct list child for
   * Primer ActionList/NavList compatibility.
   */
  children: ReactElement
  /**
   * Whether to associate the tooltip with the trigger via `aria-describedby`.
   * Defaults to `true`. Set to `false` when the trigger's accessible name is
   * already the same as `text`, so screen readers don't announce it twice
   * (once as the name and again as the description).
   */
  describeTrigger?: boolean
}

/**
 * Lightweight tooltip for collapsible sidebar items. Replaces Primer's
 * `<Tooltip>` to avoid the flash-on-hover bug caused by Primer's popover
 * `::after` bridge pseudo-element intercepting pointer events.
 *
 * Uses `popover="auto"` so the tooltip renders in the top layer
 * (escaping `overflow: clip` on the sidebar) with native Escape
 * dismissal (WCAG 1.4.13) and `pointer-events: none` so it never
 * steals mouse events from trigger elements.
 *
 * Callers are responsible for conditionally rendering this component only
 * when the sidebar is collapsed.
 */
export function SidebarTooltip({text, children, describeTrigger = true}: SidebarTooltipProps) {
  const tooltipRef = useRef<HTMLSpanElement>(null)
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const tooltipId = useId()

  const showTooltip = useCallback((e: SyntheticEvent) => {
    const trigger = e.currentTarget as HTMLElement
    openTimeoutRef.current = setTimeout(() => {
      const tooltip = tooltipRef.current
      if (!tooltip) return
      const rect = trigger.getBoundingClientRect()
      tooltip.style.top = `${rect.top + rect.height / 2}px`
      tooltip.style.left = `${rect.right + 8}px`
      tooltip.style.transform = 'translateY(-50%)'
      try {
        tooltip.showPopover()
      } catch {
        /* already open or not connected */
      }
    }, 200)
  }, [])

  const hideTooltip = useCallback(() => {
    clearTimeout(openTimeoutRef.current)
    try {
      tooltipRef.current?.hidePopover()
    } catch {
      /* already hidden or not connected */
    }
  }, [])

  useEffect(() => {
    return () => clearTimeout(openTimeoutRef.current)
  }, [])

  if (!isValidElement(children)) return children

  const childProps = children.props as Record<string, unknown>

  // eslint-disable-next-line @eslint-react/no-clone-element, react-hooks/refs
  const trigger = cloneElement(children, {
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
    'aria-describedby':
      [childProps['aria-describedby'], describeTrigger ? tooltipId : undefined].filter(Boolean).join(' ') || undefined,
  } as Record<string, unknown>)

  return (
    <>
      {trigger}
      <span ref={tooltipRef} id={tooltipId} role="tooltip" popover="auto" className={styles.sidebarTooltip}>
        {text}
      </span>
    </>
  )
}
